import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  Card,
  Row,
  Col,
  Form,
  Button,
  InputGroup,
  Table,
  Spinner,
  Pagination,
} from "react-bootstrap";

import {
  Download,
  RefreshCcw,
  Search,
  ChevronUp,
  ChevronDown,
} from "react-feather";

import * as XLSX from "xlsx";

import {
  fetchWardsThunk,
} from "../../../store/slices/wardsSlice";

import {
  fetchPendingReportThunk,
  clearPendingReport,
} from "../../../store/slices/pendingReportSlice";

import {
  printTableReport,
} from "./reportPrintUtils";

import "./ReportPage.css";

const PendingReportPage = () => {
  const dispatch = useDispatch();

  const wards = useSelector(
    (s) => s.wards?.list || []
  );

  const {
    rows = [],
    meta = {},
    loading,
    error,
  } = useSelector(
    (s) => s.pendingReport || {}
  );

  const getToday = () => {
    const d = new Date();

    const yyyy = d.getFullYear();

    const mm = String(
      d.getMonth() + 1
    ).padStart(2, "0");

    const dd = String(
      d.getDate()
    ).padStart(2, "0");

    return `${yyyy}-${mm}-${dd}`;
  };

  const [fromDate, setFromDate] = useState(
    getToday()
  );

  const [wardId, setWardId] = useState("");

  const [search, setSearch] = useState("");

  const [sortField, setSortField] =
    useState("");

  const [
    sortDirection,
    setSortDirection,
  ] = useState("asc");

  const [page, setPage] = useState(1);

  const [pageSize, setPageSize] =
    useState(20);

  useEffect(() => {
    dispatch(fetchWardsThunk());
  }, [dispatch]);

  const handleSort = (field) => {
    setSortField((currentField) => {
      if (currentField === field) {
        setSortDirection(
          (currentDirection) =>
            currentDirection === "asc"
              ? "desc"
              : "asc"
        );

        return currentField;
      }

      setSortDirection("asc");

      return field;
    });

    setPage(1);
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <span className="ms-1 report-sort-default-icon">
          <ChevronUp size={11} />
          <ChevronDown size={11} />
        </span>
      );
    }

    return sortDirection === "asc" ? (
      <ChevronUp
        size={14}
        className="ms-1"
      />
    ) : (
      <ChevronDown
        size={14}
        className="ms-1"
      />
    );
  };

  const filteredRows = useMemo(() => {
    const q = search
      .trim()
      .toLowerCase();

    const searchedRows = !q
      ? [...rows]
      : rows.filter((r) => {
          const searchableText = `
            ${r.home_id || ""}
            ${r.qr_code || ""}
            ${r.door_no || ""}
            ${r.family_head || ""}
            ${r.ward_name || ""}
            ${r.mobile || ""}
            ${r.balance || ""}
          `.toLowerCase();

          return searchableText.includes(q);
        });

    if (!sortField) {
      return searchedRows;
    }

    const numericFields = [
      "home_id",
      "mobile",
      "balance",
    ];

    return [...searchedRows].sort(
      (a, b) => {
        let aValue = a?.[sortField];
        let bValue = b?.[sortField];

        if (
          numericFields.includes(sortField)
        ) {
          aValue = Number(aValue || 0);
          bValue = Number(bValue || 0);

          return sortDirection === "asc"
            ? aValue - bValue
            : bValue - aValue;
        }

        const comparison = String(
          aValue || ""
        ).localeCompare(
          String(bValue || ""),
          undefined,
          {
            numeric: true,
            sensitivity: "base",
          }
        );

        return sortDirection === "asc"
          ? comparison
          : -comparison;
      }
    );
  }, [
    rows,
    search,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    setPage(1);
  }, [
    fromDate,
    wardId,
    search,
    rows.length,
  ]);

  const total = filteredRows.length;

  const totalPages = Math.max(
    1,
    Math.ceil(total / pageSize)
  );

  const currentPage = Math.min(
    page,
    totalPages
  );

  const pagedRows = useMemo(() => {
    const start =
      (currentPage - 1) * pageSize;

    return filteredRows.slice(
      start,
      start + pageSize
    );
  }, [
    filteredRows,
    currentPage,
    pageSize,
  ]);

  const handleLoad = () => {
    dispatch(
      fetchPendingReportThunk({
        from_date: fromDate,
        ward_id: wardId,
      })
    );
  };

  const handleClear = () => {
    setFromDate(getToday());
    setWardId("");
    setSearch("");
    setSortField("");
    setSortDirection("asc");
    setPage(1);

    dispatch(clearPendingReport());
  };

  const exportExcel = () => {
    const data = filteredRows.map(
      (r, idx) => ({
        "#": idx + 1,
        "House Code": r.qr_code,
        "Door No": r.door_no,
        "Family Head": r.family_head,
        Ward: r.ward_name,
        Mobile: r.mobile,
        Balance: r.balance,
      })
    );

    const ws =
      XLSX.utils.json_to_sheet(data);

    const wb =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      ws,
      "Pending Report"
    );

    XLSX.writeFile(
      wb,
      `pending_report_${fromDate}_to_${getToday()}.xlsx`
    );
  };

  const exportPDF = () => {
    const data = filteredRows.map(
      (r, idx) => ({
        "#": idx + 1,
        "House Code": r.qr_code ?? "",
        "Door No": r.door_no ?? "",
        "Family Head":
          r.family_head ?? "",
        Ward: r.ward_name ?? "",
        Mobile: r.mobile ?? "",
        Balance: r.balance ?? "",
      })
    );

    printTableReport({
      title:
        "Water Bill - Pending Report",

      fileName:
        "pending_report.pdf",

      data,

      metaLines: [
        `From Date: ${
          meta?.from_date ||
          fromDate ||
          "-"
        }`,
        `To Date: ${
          meta?.to_date ||
          getToday()
        }`,
        `Total Records: ${filteredRows.length}`,
      ],
    });
  };

  const renderPagination = () => {
    if (totalPages <= 1) {
      return null;
    }

    const items = [];

    const maxButtons = 5;

    let start = Math.max(
      1,
      currentPage -
        Math.floor(maxButtons / 2)
    );

    let end = Math.min(
      totalPages,
      start + maxButtons - 1
    );

    start = Math.max(
      1,
      end - maxButtons + 1
    );

    items.push(
      <Pagination.Prev
        key="prev"
        disabled={currentPage === 1}
        onClick={() =>
          setPage((p) =>
            Math.max(1, p - 1)
          )
        }
      />
    );

    for (
      let p = start;
      p <= end;
      p++
    ) {
      items.push(
        <Pagination.Item
          key={p}
          active={p === currentPage}
          onClick={() => setPage(p)}
        >
          {p}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        disabled={
          currentPage === totalPages
        }
        onClick={() =>
          setPage((p) =>
            Math.min(
              totalPages,
              p + 1
            )
          )
        }
      />
    );

    return (
      <Pagination className="mb-0">
        {items}
      </Pagination>
    );
  };

  return (
    <div className="report-page">
      <Card className="report-card">
        <Card.Header className="report-card-header">
          <div className="report-header-row">
            <div>
              <div className="report-title">
                Water Bill - Pending Report
              </div>
            </div>
          </div>
        </Card.Header>

        <Card.Body>
          {error ? (
            <div className="alert alert-danger mb-3">
              {error}
            </div>
          ) : null}

          <div className="report-filter-box">
            <Row className="g-3">
              <Col lg={3} md={6}>
                <Form.Label className="report-label">
                  From Date
                </Form.Label>

                <Form.Control
                  type="date"
                  value={fromDate}
                  max={getToday()}
                  onChange={(e) =>
                    setFromDate(
                      e.target.value
                    )
                  }
                />
              </Col>

              <Col lg={3} md={6}>
                <Form.Label className="report-label">
                  To Date
                </Form.Label>

                <Form.Control
                  type="date"
                  value={getToday()}
                  disabled
                  readOnly
                />
              </Col>

              <Col lg={3} md={6}>
                <Form.Label className="report-label">
                  Ward
                </Form.Label>

                <Form.Select
                  value={wardId}
                  onChange={(e) =>
                    setWardId(
                      e.target.value
                    )
                  }
                >
                  <option value="">
                    Select Ward
                  </option>

                  {wards.map((w) => (
                    <option
                      key={w.ward_id}
                      value={String(
                        w.ward_id
                      )}
                    >
                      {w.ward_name}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col
                lg={12}
                className="d-flex align-items-end"
              >
                <div className="report-filter-actions">
                  <Button
                    className="report-btn-primary"
                    variant="primary"
                    onClick={handleLoad}
                    disabled={
                      loading ||
                      !fromDate
                    }
                    title={
                      !fromDate
                        ? "Select from date first"
                        : ""
                    }
                  >
                    {loading ? (
                      <>
                        <Spinner
                          size="sm"
                          className="me-2"
                        />
                        Loading
                      </>
                    ) : (
                      "Load"
                    )}
                  </Button>

                  <Button
                    className="report-btn"
                    variant="outline-secondary"
                    onClick={handleClear}
                  >
                    <RefreshCcw
                      size={16}
                      className="me-2"
                    />
                    Clear
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          <div className="report-table-toolbar">
            <div className="report-table-title">
              Pending Houses{" "}
              <span className="report-count">
                (
                {total.toLocaleString()}{" "}
                records)
              </span>
            </div>

            <div className="report-table-right">
              <InputGroup className="report-search">
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>

                <Form.Control
                  value={search}
                  onChange={(e) =>
                    setSearch(
                      e.target.value
                    )
                  }
                  placeholder="Search house / door / name / ward / mobile..."
                />
              </InputGroup>

              <Button
                className="report-btn"
                variant="outline-primary"
                onClick={exportExcel}
                disabled={
                  !filteredRows.length
                }
              >
                <Download
                  size={16}
                  className="me-2"
                />
                Excel
              </Button>

              <Button
                className="report-btn"
                variant="outline-danger"
                onClick={exportPDF}
                disabled={
                  !filteredRows.length
                }
              >
                <Download
                  size={16}
                  className="me-2"
                />
                PDF
              </Button>
            </div>
          </div>

          <div className="report-table-wrap">
            <div className="report-table-scroll">
              <Table
                hover
                className="mb-0 report-table"
              >
                <thead>
                  <tr>
                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField ===
                          "home_id"
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() =>
                          handleSort(
                            "home_id"
                          )
                        }
                      >
                        #{" "}
                        {renderSortIcon(
                          "home_id"
                        )}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField ===
                          "qr_code"
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() =>
                          handleSort(
                            "qr_code"
                          )
                        }
                      >
                        House Code{" "}
                        {renderSortIcon(
                          "qr_code"
                        )}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField ===
                          "door_no"
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() =>
                          handleSort(
                            "door_no"
                          )
                        }
                      >
                        Door No{" "}
                        {renderSortIcon(
                          "door_no"
                        )}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField ===
                          "family_head"
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() =>
                          handleSort(
                            "family_head"
                          )
                        }
                      >
                        Family Head{" "}
                        {renderSortIcon(
                          "family_head"
                        )}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField ===
                          "ward_name"
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() =>
                          handleSort(
                            "ward_name"
                          )
                        }
                      >
                        Ward{" "}
                        {renderSortIcon(
                          "ward_name"
                        )}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField ===
                          "mobile"
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() =>
                          handleSort(
                            "mobile"
                          )
                        }
                      >
                        Mobile{" "}
                        {renderSortIcon(
                          "mobile"
                        )}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField ===
                          "balance"
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() =>
                          handleSort(
                            "balance"
                          )
                        }
                      >
                        Balance{" "}
                        {renderSortIcon(
                          "balance"
                        )}
                      </button>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="text-center py-4"
                      >
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Loading...
                      </td>
                    </tr>
                  ) : pagedRows.length ? (
                    pagedRows.map(
                      (r, idx) => (
                        <tr
                          key={String(
                            r.home_id ||
                              r.qr_code ||
                              idx
                          )}
                        >
                          <td>
                            {(currentPage -
                              1) *
                              pageSize +
                              idx +
                              1}
                          </td>

                          <td>
                            {r.qr_code}
                          </td>

                          <td>
                            {r.door_no}
                          </td>

                          <td>
                            {
                              r.family_head
                            }
                          </td>

                          <td>
                            {r.ward_name}
                          </td>

                          <td>
                            {r.mobile}
                          </td>

                          <td>
                            {r.balance}
                          </td>
                        </tr>
                      )
                    )
                  ) : (
                    <tr>
                      <td
                        colSpan="7"
                        className="text-center py-4"
                      >
                        No data found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="report-pagination-bar">
              <div className="report-page-size">
                <span>Rows:</span>

                <Form.Select
                  value={pageSize}
                  onChange={(e) =>
                    setPageSize(
                      Number(
                        e.target.value
                      )
                    )
                  }
                  className="page-size-select"
                >
                  {[
                    10,
                    20,
                    50,
                    100,
                  ].map((n) => (
                    <option
                      key={n}
                      value={n}
                    >
                      {n}
                    </option>
                  ))}
                </Form.Select>
              </div>

              <div className="report-pagination">
                {renderPagination()}
              </div>

              <div className="report-page-info">
                Page {currentPage} /{" "}
                {totalPages}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default PendingReportPage;